import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Simplebar } from "../Simplebar/Simplebar";
import { AnimatedIconButton } from "../AnimatedIconButton/AnimatedIconButton";

const PREVIEW_URL = "/Commons/Pages/pantalla_documento_preview.aspx";

const EXTENSIONES_IMAGEN = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg", "ico"];
const EXTENSIONES_TEXTO = ["txt", "csv", "json", "xml", "log", "md"];

const TIPO = {
    PDF: "pdf",
    IMAGEN: "imagen",
    TEXTO: "texto",
    NO_SOPORTADO: "noSoportado",
};

const normalizarExtension = (extension) =>
    (extension || "").toString().trim().replace(/^\./, "").toLowerCase();

const getTipoVista = (extension) => {
    const ext = normalizarExtension(extension);
    if (ext === "pdf") return TIPO.PDF;
    if (EXTENSIONES_IMAGEN.includes(ext)) return TIPO.IMAGEN;
    if (EXTENSIONES_TEXTO.includes(ext)) return TIPO.TEXTO;
    return TIPO.NO_SOPORTADO;
};

const getUrlDocumento = (idSec) =>
    `${PREVIEW_URL}?idSec=${encodeURIComponent(idSec)}`;

/**
 * Visor inline de documentos adjuntos. Se monta en el shell (ver Navbar.js) y
 * expone window.Citrus.DocumentoPreview.show(idSec, nombre, extension) para que
 * ucDocumentos lo dispare desde WebForms (vía window.top).
 */
export const DocumentoPreview = () => {
    const simplebarRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [documento, setDocumento] = useState({ idSec: null, nombre: "", extension: "" });
    const [textoContenido, setTextoContenido] = useState("");
    const [error, setError] = useState(false);
    const [openSeq, setOpenSeq] = useState(0);

    const tipoVista = getTipoVista(documento.extension);
    const url = documento.idSec != null ? getUrlDocumento(documento.idSec) : "";

    const show = useCallback((idSec, nombre, extension) => {
        setError(false);
        setTextoContenido("");
        setDocumento({ idSec, nombre: nombre || "Documento", extension });
        // Incrementar la secuencia fuerza el remontaje del iframe/img en cada apertura,
        // asi onLoad vuelve a dispararse y limpia el spinner aunque se reabra el mismo documento.
        setOpenSeq((s) => s + 1);
        setVisible(true);
    }, []);

    // Registrar el trigger global para que ucDocumentos (WebForms) lo invoque.
    useEffect(() => {
        window.Citrus = window.Citrus || {};
        window.Citrus.DocumentoPreview = { show };
        return () => {
            if (window.Citrus && window.Citrus.DocumentoPreview) {
                delete window.Citrus.DocumentoPreview;
            }
        };
    }, [show]);

    // Cargar contenido de texto cuando aplica.
    useEffect(() => {
        if (!visible || tipoVista !== TIPO.TEXTO || documento.idSec == null) return;

        let cancelado = false;
        setError(false);
        setTextoContenido("");
        simplebarRef.current?.showLoading(true);

        fetch(url, { credentials: "same-origin" })
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.text();
            })
            .then((texto) => {
                if (!cancelado) setTextoContenido(texto);
            })
            .catch(() => {
                if (!cancelado) setError(true);
            })
            .finally(() => {
                if (!cancelado) simplebarRef.current?.showLoading(false);
            });

        return () => {
            cancelado = true;
        };
    }, [visible, tipoVista, url, documento.idSec, openSeq]);

    // Mostrar spinner mientras carga el iframe/imagen.
    useEffect(() => {
        if (!visible) return;
        if (tipoVista === TIPO.PDF || tipoVista === TIPO.IMAGEN) {
            simplebarRef.current?.showLoading(true);
        }
    }, [visible, tipoVista, url, openSeq]);

    const onCargado = () => simplebarRef.current?.showLoading(false);
    const onErrorCarga = () => {
        setError(true);
        simplebarRef.current?.showLoading(false);
    };

    // El endpoint sirve el archivo inline; el atributo download (mismo origen) fuerza la descarga.
    const descargar = () => {
        if (documento.idSec == null) return;
        const a = document.createElement("a");
        a.href = url;
        a.download = documento.nombre || "documento";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const contenedorStyle = {
        width: "100%",
        height: "85vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "auto",
    };

    const renderContenido = () => {
        if (documento.idSec == null) return null;

        if (error) {
            return renderNoSoportado("No fue posible cargar la vista previa del documento.");
        }

        switch (tipoVista) {
            case TIPO.PDF:
                return (
                    <iframe
                        key={openSeq}
                        title={documento.nombre}
                        src={url}
                        style={{ width: "100%", height: "85vh", border: "none" }}
                        onLoad={onCargado}
                        onError={onErrorCarga}
                    />
                );
            case TIPO.IMAGEN:
                return (
                    <img
                        key={openSeq}
                        alt={documento.nombre}
                        src={url}
                        style={{ maxWidth: "100%", maxHeight: "85vh", objectFit: "contain" }}
                        onLoad={onCargado}
                        onError={onErrorCarga}
                    />
                );
            case TIPO.TEXTO:
                return (
                    <pre
                        style={{
                            width: "100%",
                            height: "85vh",
                            margin: 0,
                            padding: "12px",
                            overflow: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontFamily: "monospace",
                            fontSize: "13px",
                            textAlign: "left",
                        }}
                    >
                        {textoContenido}
                    </pre>
                );
            default:
                return renderNoSoportado("Vista previa no disponible para este tipo de archivo.");
        }
    };

    const renderNoSoportado = (mensaje) => (
        <div style={{ textAlign: "center", padding: "24px" }}>
            <p style={{ fontSize: "18px", fontWeight: "bold" }}>{mensaje}</p>
            <div className="d-flex justify-content-center">
                <AnimatedIconButton
                    onClick={descargar}
                    iconName="download"
                    message="Descargar"
                    size="45px"
                    height="24px"
                    width="24px"
                />
            </div>
        </div>
    );

    // Mostrar el boton de descarga en el footer solo al previsualizar una imagen.
    const mostrarDescarga = tipoVista === TIPO.IMAGEN && !error && documento.idSec != null;
    const btnDescarga = mostrarDescarga
        ? {
              show: true,
              isComponent: true,
              isCenter: true,
              iconName: "download", // "import",
              message: "Descargar",
              size: "45px",
              height: "24px",
              width: "24px",
              func: descargar,
          }
        : { show: false, label: "", func: () => {} };

    const simplebar = (
        <Simplebar
            ref={simplebarRef}
            size="xxl"
            placement="end"
            backdrop={true}
            portal={false}
            useLoading={true}
            showScrollY={true}
            visible={visible}
            setShow={setVisible}
            title={documento.nombre}
            btnLeft={btnDescarga}
            btnRight={{ show: false, label: "", func: () => {} }}
        >
            <div style={contenedorStyle}>{renderContenido()}</div>
        </Simplebar>
    );

    // Renderizar dentro del contenedor con estilos del SPA (igual que SupportButton).
    const sidebarContainer =
        typeof document !== "undefined" ? document.querySelector("#sidebarContainer") : null;

    if (sidebarContainer) {
        return createPortal(simplebar, sidebarContainer);
    }
    return <div className="bootstrapiso">{simplebar}</div>;
};
